import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Mail, Phone, MapPin, Clock, ArrowUp } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { useI18n } from "@/lib/i18n";
import { HOME_SECTIONS, langPath, stripLang } from "@/lib/routes";

export const Footer = ({ onOpenCookiePreferences }) => {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = stripLang(location.pathname) === "";
  const year = new Date().getFullYear();

  const goToSection = (id) => {
    const scroll = () => {
      const el = document.getElementById(id);
      if (el) {
        const top = el.getBoundingClientRect().top + window.pageYOffset - 82;
        window.scrollTo({ top, behavior: "smooth" });
      }
    };
    if (!isHome) {
      navigate(langPath(lang));
      window.setTimeout(scroll, 220);
    } else {
      scroll();
    }
  };

  const legal = [
    t("footer.legal.companyName"),
    t("footer.legal.registration"),
    t("footer.legal.taxId"),
    t("footer.legal.incorporation"),
    t("footer.legal.license"),
    t("footer.legal.license2"),
    t("footer.legal.jurisdiction"),
  ];

  return (
    <footer
      data-testid="site-footer"
      className="mt-2 border-t border-hairline bg-paper2"
    >
      <div className="mx-auto w-full max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid gap-10 md:grid-cols-12">
          <div className="md:col-span-4">
            <Link
              to={langPath(lang)}
              data-testid="footer-logo-link"
              className="inline-flex items-center gap-2.5"
            >
              <LogoMark className="h-9 w-9" title={t("common.logoAlt")} />
              <span className="text-[1.05rem] font-semibold text-ink">
                {t("common.brand")}
              </span>
            </Link>
            <p className="mt-4 max-w-[42ch] text-[0.85rem] leading-6 text-muted-ink">
              {t("footer.tagline")}
            </p>
            <button
              type="button"
              onClick={onOpenCookiePreferences}
              data-testid="footer-cookie-settings"
              className="mt-5 inline-flex items-center rounded-lg border border-hairline bg-white px-3 py-2 text-[0.78rem] font-medium text-ink transition-colors duration-200 hover:bg-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber"
            >
              {t("footer.cookieSettings")}
            </button>
          </div>

          <div className="md:col-span-3">
            <h2 className="font-mono text-[0.6rem] uppercase tracking-[0.18em] text-muted-ink/70">
              {t("footer.navTitle")}
            </h2>
            <ul className="mt-4 space-y-2.5">
              <li>
                <Link
                  to={langPath(lang)}
                  data-testid="footer-nav-home"
                  className="link-underline text-[0.85rem] text-muted-ink hover:text-ink"
                >
                  {t("nav.home")}
                </Link>
              </li>
              {HOME_SECTIONS.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => goToSection(s.id)}
                    data-testid={`footer-nav-${s.id}`}
                    className="link-underline text-left text-[0.85rem] text-muted-ink hover:text-ink"
                  >
                    {t(s.labelKey)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <h2 className="font-mono text-[0.6rem] uppercase tracking-[0.18em] text-muted-ink/70">
              {t("footer.legalTitle")}
            </h2>
            <ul className="mt-4 space-y-2.5">
              <li>
                <Link
                  to={langPath(lang, "privacy")}
                  data-testid="footer-privacy-link"
                  className="link-underline text-[0.85rem] text-muted-ink hover:text-ink"
                >
                  {t("nav.privacy")}
                </Link>
              </li>
              <li>
                <Link
                  to={langPath(lang, "cookies")}
                  data-testid="footer-cookies-link"
                  className="link-underline text-[0.85rem] text-muted-ink hover:text-ink"
                >
                  {t("nav.cookies")}
                </Link>
              </li>
              <li>
                <Link
                  to={langPath(lang, "terms")}
                  data-testid="footer-terms-link"
                  className="link-underline text-[0.85rem] text-muted-ink hover:text-ink"
                >
                  {t("nav.terms")}
                </Link>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <h2 className="font-mono text-[0.6rem] uppercase tracking-[0.18em] text-muted-ink/70">
              {t("footer.contactTitle")}
            </h2>
            <ul className="mt-4 space-y-3 text-[0.85rem] text-muted-ink">
              <li className="flex gap-2.5">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-amber" strokeWidth={1.75} />
                <a
                  href={`mailto:${t("footer.emailGeneral")}`}
                  data-testid="footer-email-general"
                  className="link-underline hover:text-ink"
                >
                  {t("footer.emailGeneral")}
                </a>
              </li>
              <li className="flex gap-2.5">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-amber" strokeWidth={1.75} />
                <a
                  href={`tel:${t("footer.phone").replace(/\s/g, "")}`}
                  data-testid="footer-phone"
                  className="link-underline hover:text-ink"
                >
                  {t("footer.phone")}
                </a>
              </li>
              <li className="flex gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-amber" strokeWidth={1.75} />
                <span>{t("footer.address")}</span>
              </li>
              <li className="flex gap-2.5">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber" strokeWidth={1.75} />
                <span>{t("footer.hours")}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 h-px bg-hairline" />

        <div className="mt-6 grid gap-6 md:grid-cols-12">
          <div className="md:col-span-8">
            <h2 className="font-mono text-[0.6rem] uppercase tracking-[0.18em] text-muted-ink/70">
              {t("footer.companyTitle")}
            </h2>
            <ul
              data-testid="footer-legal-details"
              className="mt-3 space-y-1.5 text-[0.76rem] leading-5 text-muted-ink/90"
            >
              {legal.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-4">
            <p className="text-[0.72rem] leading-5 text-muted-ink/80">
              {t("footer.disclaimer")}
            </p>
          </div>
        </div>

        <div className="mt-8 flex flex-col items-start justify-between gap-4 border-t border-hairline pt-6 sm:flex-row sm:items-center">
          <p className="text-[0.75rem] text-muted-ink/80">
            © {year} {t("common.brandFull")} · {t("common.domain")} ·{" "}
            {t("footer.rights")}
          </p>
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            data-testid="footer-back-to-top"
            className="inline-flex items-center gap-1.5 rounded-lg border border-hairline bg-white px-3 py-2 text-[0.75rem] font-medium text-ink transition-colors duration-200 hover:bg-paper"
          >
            <ArrowUp className="h-3.5 w-3.5" strokeWidth={2} />
            {t("footer.backToTop")}
          </button>
        </div>
      </div>
    </footer>
  );
};
